import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { http, fmtRON, API } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Package, ShoppingBag, Mail, MapPin, Trash2, Upload, Download, ImageIcon, Plus, X } from "lucide-react";

/** Simple reusable image uploader -> returns absolute URL. */
function ImageUpload({ onUploaded, label = "Încarcă imagine" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await http.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Build absolute URL served by backend proxy
      const absolute = `${API}${data.url.replace(/^\/api/, "")}`.replace(API + "/files", API + "/files");
      // backend returns /api/files/<path>, prefix with REACT_APP_BACKEND_URL
      const base = process.env.REACT_APP_BACKEND_URL;
      const url = `${base}${data.url}`;
      onUploaded(url);
      toast.success("Imagine încărcată");
    } catch (err) {
      toast.error("Eroare upload");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" onChange={onFile} className="hidden" />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="btn-dark text-xs inline-flex items-center gap-2">
        <Upload size={12} /> {busy ? "Se încarcă..." : label}
      </button>
    </>
  );
}

const EMPTY_PRODUCT = {
  slug: "", title: "", subtitle: "", short_description: "", description: "",
  price: 45, original_price: null, pages: 48, format: "A4 (21 x 29.7 cm)",
  collection: "principala", collection_label: "", is_bundle: false, bundle_items: [],
  cover_image: "", gallery: [], stock: 100, featured: false, seasonal: false, sort_order: 10,
};

export default function AdminDashboardPage() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [lockers, setLockers] = useState([]);
  const [gallery, setGallery] = useState([]);

  const [editing, setEditing] = useState(null);  // product being edited (null = none)
  const [creating, setCreating] = useState(false); // new product modal
  const [newProduct, setNewProduct] = useState(EMPTY_PRODUCT);
  const [newLocker, setNewLocker] = useState({ type: "easybox", code: "", city: "", address: "" });
  const [newGalleryImg, setNewGalleryImg] = useState({ url: "", label: "", category: "coperti", sort_order: 50 });
  const [importMode, setImportMode] = useState("merge");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!checking && !user) nav("/admin/login");
  }, [checking, user, nav]);

  const loadAll = async () => {
    try {
      const [p, o, m, l, g] = await Promise.all([
        http.get("/products"),
        http.get("/admin/orders"),
        http.get("/admin/contact"),
        http.get("/lockers"),
        http.get("/gallery"),
      ]);
      setProducts(p.data); setOrders(o.data); setMessages(m.data); setLockers(l.data); setGallery(g.data);
    } catch {}
  };
  useEffect(() => { if (user) loadAll(); }, [user]);

  // Products
  const saveProduct = async () => {
    if (!editing) return;
    try {
      await http.put(`/admin/products/${editing.slug}`, editing);
      toast.success("Produs actualizat");
      setEditing(null); loadAll();
    } catch { toast.error("Eroare la salvare"); }
  };

  const createProduct = async () => {
    if (!newProduct.slug || !newProduct.title || !newProduct.cover_image) {
      toast.error("Slug, titlu și copertă sunt obligatorii"); return;
    }
    try {
      await http.post("/admin/products", { ...newProduct, slug: newProduct.slug.toLowerCase().replace(/\s+/g, "-") });
      toast.success("Carte adăugată");
      setCreating(false); setNewProduct(EMPTY_PRODUCT); loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la creare");
    }
  };

  const deleteProduct = async (slug, title) => {
    if (!window.confirm(`Sigur ștergi "${title}"? Nu se poate recupera.`)) return;
    try {
      await http.delete(`/admin/products/${slug}`);
      toast.success("Carte ștearsă"); loadAll();
    } catch { toast.error("Eroare"); }
  };

  // Orders
  const updateOrderStatus = async (id, status) => {
    try {
      await http.patch(`/admin/orders/${id}/status`, null, { params: { status } });
      toast.success("Status actualizat"); loadAll();
    } catch { toast.error("Eroare"); }
  };

  // Lockers
  const addLocker = async (e) => {
    e.preventDefault();
    const code = newLocker.code.trim().toUpperCase();
    if (!code || !newLocker.city.trim() || !newLocker.address.trim()) { toast.error("Completează toate câmpurile"); return; }
    try {
      await http.post("/admin/lockers", { ...newLocker, code });
      toast.success("Locker adăugat"); setNewLocker({ type: newLocker.type, code: "", city: "", address: "" }); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Eroare la salvare"); }
  };
  const deleteLocker = async (id) => {
    if (!window.confirm("Sigur vrei să ștergi acest locker?")) return;
    try { await http.delete(`/admin/lockers/${id}`); toast.success("Locker șters"); loadAll(); } catch { toast.error("Eroare"); }
  };
  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (importMode === "replace" && !window.confirm("Modul 'Înlocuiește' șterge toate locker-ele existente. Continui?")) { e.target.value = ""; return; }
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await http.post("/admin/lockers/import", fd, { params: { mode: importMode }, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import gata: ${data.imported} adăugate, ${data.skipped} ignorate. Total: ${data.total}`);
      if (data.errors?.length) toast.warning(`Erori: ${data.errors.join(", ")}`);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Eroare import"); }
    finally { setImporting(false); e.target.value = ""; }
  };
  const downloadSampleCsv = () => {
    const sample = "type,code,city,address\neasybox,EB-BUC-100,București,Mega Image Pache Protopopescu 15\nfanbox,FB-CJN-050,Cluj-Napoca,Kaufland Someșeni Bd. Muncii 150\n";
    const blob = new Blob([sample], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "vis-de-zi-lockere-sample.csv"; a.click(); URL.revokeObjectURL(url);
  };

  // Gallery
  const addGalleryImage = async () => {
    if (!newGalleryImg.url) { toast.error("Încarcă sau lipește URL-ul imaginii"); return; }
    try {
      await http.post("/admin/gallery", newGalleryImg);
      toast.success("Imagine adăugată în galerie");
      setNewGalleryImg({ url: "", label: "", category: newGalleryImg.category, sort_order: 50 });
      loadAll();
    } catch { toast.error("Eroare"); }
  };
  const deleteGalleryImage = async (id, label) => {
    if (!window.confirm(`Sigur ștergi "${label || "această imagine"}"?`)) return;
    try { await http.delete(`/admin/gallery/${id}`); toast.success("Imagine ștearsă"); loadAll(); } catch { toast.error("Eroare"); }
  };

  if (checking || !user) return <div className="py-32 text-center">Se încarcă...</div>;

  return (
    <div className="py-10" data-testid="admin-dashboard">
      <div className="container-vdz">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="eyebrow mb-2">Admin</div>
            <h1 className="font-serif-display text-4xl" style={{ color: "var(--vdz-navy)" }}>Bună, {user.name || "Alina"} ✨</h1>
          </div>
          <button onClick={() => { logout(); nav("/"); }} className="btn-dark flex items-center gap-2" data-testid="admin-logout-button">
            <LogOut size={14} /> Ieșire
          </button>
        </div>

        <div className="flex gap-2 border-b mb-8 overflow-x-auto" style={{ borderColor: "var(--vdz-cream-border)" }}>
          {[
            { id: "products", label: "Produse", icon: Package, count: products.length },
            { id: "gallery", label: "Galerie", icon: ImageIcon, count: gallery.length },
            { id: "orders", label: "Comenzi", icon: ShoppingBag, count: orders.length },
            { id: "messages", label: "Mesaje", icon: Mail, count: messages.length },
            { id: "lockers", label: "Locker-e", icon: MapPin, count: lockers.length },
          ].map(({ id, label, icon: Icon, count }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`px-5 py-3 flex items-center gap-2 font-serif-accent uppercase tracking-[0.18em] text-xs whitespace-nowrap ${
                tab === id ? "border-b-2 border-[color:var(--vdz-gold)] text-[color:var(--vdz-navy)]" : "opacity-60 hover:opacity-100"
              }`}>
              <Icon size={14} /> {label} <span className="opacity-50">({count})</span>
            </button>
          ))}
        </div>

        {/* PRODUCTS TAB */}
        {tab === "products" && (
          <div data-testid="admin-products-section">
            <div className="flex justify-end mb-4">
              <button onClick={() => { setNewProduct(EMPTY_PRODUCT); setCreating(true); }} className="btn-gold inline-flex items-center gap-2" data-testid="admin-new-product-button">
                <Plus size={14} /> Carte nouă
              </button>
            </div>
            <div className="space-y-3" data-testid="admin-products-list">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 border bg-white" style={{ borderColor: "var(--vdz-cream-border)" }}>
                  <img src={p.cover_image} alt="" className="w-14 h-20 object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-lg truncate">{p.title}</div>
                    <div className="text-xs opacity-70">{p.collection_label} · Stoc: {p.stock} · {fmtRON(p.price)} · Galerie: {(p.gallery || []).length}</div>
                  </div>
                  <button onClick={() => setEditing({ ...p, gallery: [...(p.gallery || [])] })} className="btn-dark text-xs" data-testid={`admin-edit-${p.slug}`}>Editează</button>
                  <button onClick={() => deleteProduct(p.slug, p.title)} className="p-2 hover:text-[color:var(--vdz-burgundy)]" aria-label="Șterge" data-testid={`admin-delete-product-${p.slug}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* GALLERY TAB */}
        {tab === "gallery" && (
          <div data-testid="admin-gallery-section">
            {/* Add form */}
            <div className="p-5 border bg-white mb-6" style={{ borderColor: "var(--vdz-cream-border)" }} data-testid="admin-add-gallery-form">
              <div className="eyebrow mb-3">Adaugă imagine în galeria publică</div>
              <div className="grid gap-3 md:grid-cols-4">
                <select value={newGalleryImg.category} onChange={(e) => setNewGalleryImg({ ...newGalleryImg, category: e.target.value })} className="vdz-input">
                  <option value="coperti">Coperți</option>
                  <option value="linii">Pagini de colorat</option>
                  <option value="colorate">Exemple colorate</option>
                  <option value="mockups">Atmosferă</option>
                </select>
                <input className="vdz-input" placeholder="Etichetă (ex: Pagină sirenă)" value={newGalleryImg.label} onChange={(e) => setNewGalleryImg({ ...newGalleryImg, label: e.target.value })} />
                <input className="vdz-input" placeholder="Ordine (ex: 10)" type="number" value={newGalleryImg.sort_order} onChange={(e) => setNewGalleryImg({ ...newGalleryImg, sort_order: parseInt(e.target.value) || 0 })} />
                <div className="flex items-center gap-2">
                  <ImageUpload onUploaded={(url) => setNewGalleryImg({ ...newGalleryImg, url })} label="Alege poză" />
                  <button onClick={addGalleryImage} className="btn-gold text-xs flex-1">Adaugă</button>
                </div>
              </div>
              {newGalleryImg.url && (
                <div className="mt-3 flex items-center gap-3">
                  <img src={newGalleryImg.url} alt="preview" className="w-16 h-16 object-cover" />
                  <input className="vdz-input flex-1 text-xs" readOnly value={newGalleryImg.url} />
                </div>
              )}
            </div>

            {/* List */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-gallery-list">
              {gallery.map((img) => (
                <div key={img.id} className="relative group border bg-white" style={{ borderColor: "var(--vdz-cream-border)" }}>
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={img.url} alt={img.label || ""} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-serif-accent gold-text">{img.category}</div>
                    <div className="font-serif-display text-sm leading-tight mt-1 truncate">{img.label || "(fără etichetă)"}</div>
                  </div>
                  <button onClick={() => deleteGalleryImage(img.id, img.label)} className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white hover:text-[color:var(--vdz-burgundy)] opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Șterge" data-testid={`admin-delete-gallery-${img.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {gallery.length === 0 && <p className="opacity-60 col-span-full text-center py-10">Nicio imagine în galerie.</p>}
            </div>
          </div>
        )}

        {/* ORDERS TAB */}
        {tab === "orders" && (
          <div className="space-y-3" data-testid="admin-orders-list">
            {orders.length === 0 && <p className="opacity-60 text-center py-10">Nicio comandă încă.</p>}
            {orders.map((o) => {
              const deliveryLabel = { curier: "Curier la domiciliu", easybox: "Easybox Sameday", fanbox: "FanBox Fan Courier" }[o.delivery_method] || o.delivery_method;
              const addrLine = o.locker_type
                ? `${deliveryLabel} · ${o.locker_address || "–"}${o.locker_code ? ` (cod: ${o.locker_code})` : ""}`
                : `${deliveryLabel} · ${o.address || ""}, ${o.city || ""}, ${o.county || ""} · ${o.postal_code || ""}`;
              return (
                <div key={o.id} className="p-5 border bg-white" style={{ borderColor: "var(--vdz-cream-border)" }}>
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div>
                      <div className="font-serif-display text-lg">{o.order_number}</div>
                      <div className="text-sm opacity-80">{o.first_name} {o.last_name} · {o.email} · {o.phone}</div>
                      <div className="text-xs opacity-60 mt-1">{addrLine}</div>
                      <div className="mt-2 text-xs">{o.items.map((i) => `${i.title} ×${i.quantity}`).join(" · ")}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-serif-display text-xl gold-text">{fmtRON(o.total)}</div>
                      <select value={o.status} onChange={(e) => updateOrderStatus(o.id, e.target.value)} className="vdz-input mt-2 text-xs" data-testid={`admin-order-status-${o.order_number}`}>
                        <option value="noua">Nouă</option>
                        <option value="confirmata">Confirmată</option>
                        <option value="expediata">Expediată</option>
                        <option value="livrata">Livrată</option>
                        <option value="anulata">Anulată</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* MESSAGES TAB */}
        {tab === "messages" && (
          <div className="space-y-3" data-testid="admin-messages-list">
            {messages.length === 0 && <p className="opacity-60 text-center py-10">Niciun mesaj încă.</p>}
            {messages.map((m) => (
              <div key={m.id} className="p-5 border bg-white" style={{ borderColor: "var(--vdz-cream-border)" }}>
                <div className="flex justify-between gap-4 mb-2">
                  <div><strong>{m.name}</strong> <span className="opacity-60">· {m.email}</span></div>
                  <div className="text-xs opacity-60">{new Date(m.created_at).toLocaleDateString("ro-RO")}</div>
                </div>
                <div className="font-serif-display text-lg">{m.subject}</div>
                <p className="text-sm mt-2 opacity-80">{m.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* LOCKERS TAB */}
        {tab === "lockers" && (
          <div data-testid="admin-lockers-section">
            <div className="p-5 border bg-white mb-4 flex items-center flex-wrap gap-3" style={{ borderColor: "var(--vdz-cream-border)" }} data-testid="admin-csv-import-bar">
              <div className="flex-1 min-w-[200px]">
                <div className="eyebrow mb-1">Import CSV</div>
                <p className="text-xs opacity-70">Format: <code>type,code,city,address</code> · Delimitatori: virgulă, punct-virgulă, tab</p>
              </div>
              <select value={importMode} onChange={(e) => setImportMode(e.target.value)} className="vdz-input w-auto">
                <option value="merge">Adaugă peste cele existente</option>
                <option value="replace">Înlocuiește tot</option>
              </select>
              <button onClick={downloadSampleCsv} className="btn-dark text-xs inline-flex items-center gap-2"><Download size={13} /> Model CSV</button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" />
              <button onClick={() => fileRef.current?.click()} disabled={importing} className="btn-gold text-xs inline-flex items-center gap-2"><Upload size={13} /> {importing ? "Se importă..." : "Importă CSV"}</button>
            </div>

            <form onSubmit={addLocker} className="p-5 border bg-white mb-6 grid gap-3 md:grid-cols-5" style={{ borderColor: "var(--vdz-cream-border)" }}>
              <select value={newLocker.type} onChange={(e) => setNewLocker({ ...newLocker, type: e.target.value })} className="vdz-input">
                <option value="easybox">Easybox</option>
                <option value="fanbox">FanBox</option>
              </select>
              <input className="vdz-input" placeholder="Cod (ex: EB-BUC-100)" value={newLocker.code} onChange={(e) => setNewLocker({ ...newLocker, code: e.target.value })} />
              <input className="vdz-input" placeholder="Oraș" value={newLocker.city} onChange={(e) => setNewLocker({ ...newLocker, city: e.target.value })} />
              <input className="vdz-input" placeholder="Adresă" value={newLocker.address} onChange={(e) => setNewLocker({ ...newLocker, address: e.target.value })} />
              <button type="submit" className="btn-gold">Adaugă</button>
            </form>

            <div className="space-y-3" data-testid="admin-lockers-list">
              {lockers.length === 0 && <p className="opacity-60 text-center py-10">Niciun locker.</p>}
              {lockers.map((l) => (
                <div key={l.id} className="flex items-center gap-4 p-4 border bg-white" style={{ borderColor: "var(--vdz-cream-border)" }}>
                  <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-serif-accent" style={{ background: l.type === "easybox" ? "var(--vdz-gold)" : "var(--vdz-burgundy)", color: l.type === "easybox" ? "var(--vdz-navy)" : "var(--vdz-cream)" }}>{l.type}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-base truncate">{l.address}</div>
                    <div className="text-xs opacity-60">{l.city} · Cod: {l.code}</div>
                  </div>
                  <button onClick={() => deleteLocker(l.id)} className="p-2 hover:text-[color:var(--vdz-burgundy)]" aria-label="Șterge"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EDIT PRODUCT MODAL */}
        {editing && <ProductFormModal product={editing} setProduct={setEditing} onSave={saveProduct} onClose={() => setEditing(null)} isEdit />}

        {/* CREATE PRODUCT MODAL */}
        {creating && <ProductFormModal product={newProduct} setProduct={setNewProduct} onSave={createProduct} onClose={() => setCreating(false)} isEdit={false} />}
      </div>
    </div>
  );
}

function ProductFormModal({ product, setProduct, onSave, onClose, isEdit }) {
  const update = (k, v) => setProduct({ ...product, [k]: v });
  const removeGalleryAt = (i) => update("gallery", product.gallery.filter((_, idx) => idx !== i));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-y-auto" style={{ background: "rgba(11,19,43,0.7)" }}>
      <div className="bg-white max-w-2xl w-full p-6 md:p-8 max-h-[92vh] overflow-y-auto my-6" data-testid="product-form-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="eyebrow mb-1">{isEdit ? "Editează carte" : "Carte nouă"}</div>
            <h2 className="font-serif-display text-2xl">{isEdit ? product.title : "Adaugă o carte nouă"}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:text-[color:var(--vdz-gold)]"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <input className="vdz-input" placeholder="Slug URL (ex: vis-de-zi-vara)" value={product.slug} onChange={(e) => update("slug", e.target.value)} data-testid="product-form-slug" />
              <input className="vdz-input" placeholder="Colecție slug (principala, vara...)" value={product.collection} onChange={(e) => update("collection", e.target.value)} />
            </div>
          )}
          <input className="vdz-input" placeholder="Titlu" value={product.title || ""} onChange={(e) => update("title", e.target.value)} data-testid="product-form-title" />
          <input className="vdz-input" placeholder="Subtitlu" value={product.subtitle || ""} onChange={(e) => update("subtitle", e.target.value)} />
          <input className="vdz-input" placeholder='Etichetă colecție (ex: "Ediția Principală")' value={product.collection_label || ""} onChange={(e) => update("collection_label", e.target.value)} />
          <textarea className="vdz-input" rows={2} placeholder="Descriere scurtă (apare pe cardurile din magazin)" value={product.short_description || ""} onChange={(e) => update("short_description", e.target.value)} />
          <textarea className="vdz-input" rows={5} placeholder="Descriere lungă (pagina produs)" value={product.description || ""} onChange={(e) => update("description", e.target.value)} />

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs opacity-70 mb-1 block">Preț RON</label>
              <input className="vdz-input" type="number" step="1" value={product.price || 0} onChange={(e) => update("price", parseFloat(e.target.value))} data-testid="product-form-price" />
            </div>
            <div>
              <label className="text-xs opacity-70 mb-1 block">Preț original (pt. reduceri)</label>
              <input className="vdz-input" type="number" step="1" value={product.original_price || ""} onChange={(e) => update("original_price", e.target.value ? parseFloat(e.target.value) : null)} />
            </div>
            <div>
              <label className="text-xs opacity-70 mb-1 block">Stoc</label>
              <input className="vdz-input" type="number" value={product.stock || 0} onChange={(e) => update("stock", parseInt(e.target.value))} />
            </div>
            <div>
              <label className="text-xs opacity-70 mb-1 block">Nr. desene</label>
              <input className="vdz-input" type="number" value={product.pages || 0} onChange={(e) => update("pages", parseInt(e.target.value))} />
            </div>
            <div>
              <label className="text-xs opacity-70 mb-1 block">Format</label>
              <input className="vdz-input" value={product.format || ""} onChange={(e) => update("format", e.target.value)} />
            </div>
            <div>
              <label className="text-xs opacity-70 mb-1 block">Ordine afișare</label>
              <input className="vdz-input" type="number" value={product.sort_order || 0} onChange={(e) => update("sort_order", parseInt(e.target.value))} />
            </div>
          </div>

          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!product.featured} onChange={(e) => update("featured", e.target.checked)} /> Featured (apare pe home)</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!product.seasonal} onChange={(e) => update("seasonal", e.target.checked)} /> Sezonier</label>
          </div>

          {/* Cover image */}
          <div>
            <label className="text-xs opacity-70 mb-1 block">Copertă (URL sau încarcă)</label>
            <div className="flex gap-2 items-center">
              <input className="vdz-input flex-1" placeholder="URL copertă" value={product.cover_image || ""} onChange={(e) => update("cover_image", e.target.value)} data-testid="product-form-cover" />
              <ImageUpload onUploaded={(url) => update("cover_image", url)} label="Încarcă" />
            </div>
            {product.cover_image && <img src={product.cover_image} alt="cover preview" className="mt-2 h-28 object-cover" />}
          </div>

          {/* Gallery */}
          <div>
            <label className="text-xs opacity-70 mb-2 block">Galerie produs ({(product.gallery || []).length} imagini)</label>
            <div className="flex gap-2 flex-wrap mb-3">
              {(product.gallery || []).map((url, i) => (
                <div key={i} className="relative w-20 h-28">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeGalleryAt(i)} className="absolute top-1 right-1 bg-white/90 p-1 hover:bg-white" aria-label="Șterge"><X size={12} /></button>
                </div>
              ))}
            </div>
            <ImageUpload onUploaded={(url) => update("gallery", [...(product.gallery || []), url])} label="+ Adaugă poză galerie" />
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <button onClick={onSave} className="btn-gold flex-1" data-testid="product-form-save">{isEdit ? "Salvează" : "Creează cartea"}</button>
          <button onClick={onClose} className="btn-dark">Anulează</button>
        </div>
      </div>
    </div>
  );
}
